import classNames from 'classnames';
import noop from 'lodash/noop';
import React, { createRef, forwardRef, Ref, useContext, useEffect, useMemo, useRef } from 'react';
import { Field, initialize, InjectedFormProps, reduxForm } from 'redux-form';
import uuidV4 from 'uuid/v4';
import { useDispatch } from 'react-redux';
import Button from '../Button';
import Input from '../Input';
import Select from '../Select';
import Switch from '../Switch';
import { useIds } from '../../helpers/hooks';
import CheckboxInput from '../CheckboxInput';
import ImageInput from '../ImageInput';
import ProductColorContext from '../../contexts/product-color';
import ModalHeader from '../Modal/ModalHeader';
import FormBuilderContext from 'contexts/form-builder';
import { FormBuilderProps } from 'types';

const FormComponent = forwardRef(
  ({ handleSubmit, submitting, error }: InjectedFormProps, ref: Ref<HTMLFormElement>) => {
    const {
      formFields = [],
      submit = noop,
      containerClassName = '',
      autoFocus = false,
      submitText = 'Submit',
      submitClassName = '',
      submitBtnId = uuidV4(),
      showSubmitBtn = true,
      scroll = true,
      color,
      initialValues,
    } = useContext(FormBuilderContext);
    const ids = useIds((formFields || []).length);

    return (
      <form
        ref={ref}
        onSubmit={handleSubmit(submit)}
        className={classNames(
          'flex-1 flex flex-col min-h-min-content my-8',
          { 'overflow-y-auto': scroll },
          containerClassName,
        )}
      >
        {(formFields || []).map(({ tag, class: className, id, ...prevInput }, index) => {
          const componentProps = {
            tag,
            ...prevInput,
            id: id || ids[index],
            autofocus: autoFocus && index === 0,
          };
          switch (tag) {
            case 'image': {
              return <Field key={ids[index]} {...componentProps} component={ImageInput} />;
            }
            case 'switch':
              return (
                <Field
                  key={ids[index]}
                  {...{
                    ...componentProps,
                    initialValue: componentProps.value !== undefined ? componentProps.value : '',
                  }}
                  component={Switch}
                />
              );
            case 'checkbox': {
              return <Field key={ids[index]} {...componentProps} component={CheckboxInput} />;
            }
            case 'input':
            case 'textarea':
              return (
                <Field
                  key={ids[index]}
                  {...componentProps}
                  initialValue={initialValues?.[componentProps?.name ?? ''] ?? ''}
                  component={Input}
                />
              );
            case 'select': {
              const { selectlistitems, ...restInput } = componentProps;
              const options = Object.keys(selectlistitems).map(key => ({
                label: selectlistitems[key],
                value: key,
              }));
              return <Field key={ids[index]} {...restInput} component={Select} options={options} />;
            }
            default:
              return null;
          }
        })}
        <div
          className={classNames('flex flex-1 items-end', {
            hidden: !showSubmitBtn || !formFields.length,
          })}
        >
          <Button
            type='submit'
            block
            loading={submitting}
            disabled={submitting}
            className={submitClassName || 'my-4'}
            id={submitBtnId}
            color={color}
          >
            {submitText}
          </Button>
        </div>
      </form>
    );
  },
);

const FormBuilder = (props: FormBuilderProps, upperRef: Ref<HTMLFormElement> = createRef<HTMLFormElement>()) => {
  const ref = useRef<HTMLFormElement | null>(null);
  const { formId = uuidV4(), formFields = [], initialValues: prevInitialValues = {}, ...restProps } = props;
  const color = useContext(ProductColorContext);
  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    return {
      ...formFields.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.name]: curr.value || '',
        }),
        {},
      ),
      ...prevInitialValues,
    };
  }, [formFields, prevInitialValues]);

  useEffect(() => {
    dispatch(initialize(formId, initialValues));
  }, [dispatch, formId, initialValues]);

  const Form = useMemo(
    () =>
      reduxForm({ form: formId })(props => {
        return <FormComponent ref={upperRef || ref} {...props} />;
      }),
    [formId, upperRef],
  );
  return (
    <FormBuilderContext.Provider
      value={{
        ...props,
        initialValues,
        color,
        formRef: upperRef || ref,
      }}
    >
      {restProps?.formHeader?.title && (
        <ModalHeader
          title={restProps.formHeader.title}
          description={restProps.formHeader.subTitle || ''}
          padded={false}
        />
      )}
      <Form />
    </FormBuilderContext.Provider>
  );
};

export default forwardRef<HTMLFormElement, FormBuilderProps>(FormBuilder);
